import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, Pencil, Trash2, User, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";

const usuarioVacio = () => ({ username: "", email: "", password: "" });

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null); // null = crear, object = editar
  const [form, setForm] = useState(usuarioVacio());
  const [saving, setSaving] = useState(false);

  const fetchUsuarios = async () => {
    try {
      const res = await axios.get(`${API}/usuarios`);
      setUsuarios(res.data);
    } catch {
      toast.error("Error al cargar los usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsuarios(); }, []);

  const abrirCrear = () => {
    setEditando(null);
    setForm(usuarioVacio());
    setDialogOpen(true);
  };

  const abrirEditar = (u) => {
    setEditando(u);
    setForm({ username: u.username, email: u.email, password: "" });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editando) {
        const payload = {};
        if (form.username !== editando.username) payload.username = form.username;
        if (form.email !== editando.email) payload.email = form.email;
        if (form.password.trim()) payload.password = form.password;
        await axios.put(`${API}/usuarios/${editando.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await axios.post(`${API}/usuarios`, form);
        toast.success("Usuario creado");
      }
      setDialogOpen(false);
      fetchUsuarios();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al guardar el usuario");
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async (u) => {
    if (!window.confirm(`¿Eliminar al usuario "${u.username}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/usuarios/${u.id}`);
      toast.success("Usuario eliminado");
      fetchUsuarios();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al eliminar el usuario");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Usuarios</h1>
          <p className="text-slate-500 mt-1">Gestiona los administradores del sistema</p>
        </div>
        <Button onClick={abrirCrear} className="gap-2 text-white" style={{ background: "linear-gradient(135deg, #9B1C1C, #C81E1E)" }}>
          <UserPlus className="w-4 h-4" />
          Nuevo usuario
        </Button>
      </div>

      {loading ? (
        <p className="text-slate-400">Cargando...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {usuarios.map((u) => (
            <Card key={u.id} className="border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-red-100 p-2 rounded-full">
                      <User className="w-5 h-5 text-red-700" />
                    </div>
                    <div>
                      <CardTitle className="text-base text-slate-800">{u.username}</CardTitle>
                      <p className="text-xs text-slate-500">{u.email}</p>
                    </div>
                  </div>
                  <ShieldCheck className="w-4 h-4 text-emerald-500" title="Administrador" />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-400 mb-3">
                  Creado: {new Date(u.created_at).toLocaleDateString("es-VE")}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => abrirEditar(u)}>
                    <Pencil className="w-3 h-3" />
                    Editar
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50 gap-1" onClick={() => eliminar(u)}>
                    <Trash2 className="w-3 h-3" />
                    Eliminar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {usuarios.length === 0 && (
            <p className="text-slate-400 col-span-3">No hay usuarios registrados.</p>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editando ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username">Usuario *</Label>
              <Input
                id="username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                required
                placeholder="Ej: admin"
              />
            </div>
            <div>
              <Label htmlFor="email">Correo electrónico *</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                placeholder="Ej: admin@farmavida.com"
              />
            </div>
            <div>
              <Label htmlFor="password">
                Contraseña {editando && <span className="text-slate-400 font-normal">(dejar en blanco para no cambiar)</span>}
              </Label>
              <Input
                id="password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required={!editando}
                placeholder={editando ? "••••••••" : "Mínimo 6 caracteres"}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="text-white" style={{ background: "linear-gradient(135deg, #9B1C1C, #C81E1E)" }}>
                {saving ? "Guardando..." : editando ? "Guardar cambios" : "Crear usuario"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
